import type {
  LocalHorizontal,
  LocalOrigin,
} from "../coordinates.ts";
import type {
  HorizontalBounds,
  PolygonWithHoles,
} from "../geometry.ts";
import type {
  BuildingFootprintData,
  TerrainTileData,
  WorldTileAddress,
  WorldTileId,
} from "./tile.ts";
import { WORLD_TILE_SIZE_M, worldTileId } from "./tile.ts";

export const RUNTIME_TILE_SCHEMA_VERSION = 3 as const;
export const LEGACY_RUNTIME_TILE_SCHEMA_VERSION = 2 as const;
export const RUNTIME_TILE_MANIFEST_SCHEMA_VERSION = 4 as const;
export const LEGACY_RUNTIME_TILE_MANIFEST_SCHEMA_VERSION = 3 as const;
export const RUNTIME_TILE_LOD_LEVELS = [0, 1, 2] as const;

export type RuntimeTileLodLevel =
  (typeof RUNTIME_TILE_LOD_LEVELS)[number];

export type RuntimeParkingOrientation =
  | "parallel"
  | "diagonal"
  | "perpendicular";

export interface RuntimeParkingMetadata {
  readonly side?: "left" | "right" | "both";
  readonly placement:
    | "lane"
    | "street_side"
    | "on_kerb"
    | "half_on_kerb"
    | "shoulder"
    | "parking_area"
    | "parking_space";
  readonly orientation?: RuntimeParkingOrientation;
  readonly capacity?: number;
  readonly access?: string;
  readonly restriction?: string;
  readonly staggered?: boolean;
}

export interface RuntimeLinearFeature {
  id: string;
  sourceId: string;
  kind: "road" | "path" | "railway" | "water" | "structure";
  classCode: string;
  name?: string;
  /** Official GRPK JPLOTIS: carriageway pavement width in metres. */
  carriagewayWidthM?: number;
  /** Official GRPK PLOTIS: total road width, including shoulders, in metres. */
  totalWidthM?: number;
  /** Official GRPK DANGA road-surface code. */
  surfaceCode?: string;
  /** Mapped attached-sidewalk evidence relative to this line's point order. */
  sidewalkMode?: "both" | "left" | "right" | "none" | "separate";
  sidewalkSourceId?: string;
  /** Separable OSM lane/control evidence matched offline to this GRPK line. */
  roadEvidenceSourceId?: string;
  osmWayId?: number;
  lanes?: number;
  lanesForward?: number;
  lanesBackward?: number;
  turnLanesForward?: readonly string[];
  turnLanesBackward?: readonly string[];
  changeLanesForward?: readonly string[];
  changeLanesBackward?: readonly string[];
  onewayDirection?: "forward" | "backward";
  maxspeedKph?: number;
  busLanesForward?: readonly string[];
  busLanesBackward?: readonly string[];
  cycleLaneLeft?: boolean;
  cycleLaneRight?: boolean;
  parkingLeft?: "parallel" | "marked";
  parkingRight?: "parallel" | "marked";
  /** OSM tags spatially matched offline; GRPK still owns track geometry. */
  railwayEvidenceSourceId?: string;
  railwayUsage?: "main" | "branch" | "industrial" | "military" | "tourism";
  railwayService?: "siding" | "spur" | "yard" | "crossover";
  railwayElectrified?: "contact_line" | "rail" | "no";
  railwayGaugeM?: number;
  railwayBridge?: boolean;
  railwayTunnel?: boolean;
  railwayEmbankment?: boolean;
  /** Explicit or source-derived width for a pedestrian/linear feature. */
  widthM?: number;
  /** Width spans the complete paved pedestrian-street public realm. */
  pedestrianStreetFullWidth?: boolean;
  /** Complete paved width shared with an adjoining pedestrian street. */
  publicRealmWidthM?: number;
  /** Explicit mapped height for a non-road structure, in metres. */
  heightM?: number;
  /** Source parking tags retained for deterministic offline placement. */
  parking?: RuntimeParkingMetadata;
  /** One clipped line part in metres relative to the tile origin. */
  points: readonly LocalHorizontal[];
}

export interface RuntimeAreaFeature {
  id: string;
  sourceId: string;
  kind: "land-cover" | "hardscape";
  classCode: string;
  name?: string;
  /** Source parking tags retained for deterministic offline placement. */
  parking?: RuntimeParkingMetadata;
  /** Clipped polygons in metres relative to the tile origin. */
  polygons: readonly PolygonWithHoles[];
}

export type RuntimeParkedVehicleFamily =
  | "compact-hatch"
  | "estate-wagon"
  | "ordinary-sedan"
  | "small-crossover"
  | "compact-van"
  | "older-boxy-car";

/**
 * Offline, source-linked parked-car placement. Runtime may promote any one of
 * these descriptors into the normal serializable road-vehicle simulation when
 * the player approaches it; untouched descriptors remain cheap visual data.
 */
export interface RuntimeParkedVehicleDescriptor {
  readonly id: string;
  readonly kind: "parked-vehicle";
  readonly sourceId: string;
  readonly sourceFeatureId: string;
  readonly provenance:
    | "osm-explicit-parking-space"
    | "osm-explicit-street-parking"
    | "osm-mapped-parking-area"
    | "official-orthophoto-reviewed-row"
    | "official-orthophoto-detected-vehicle"
    | "official-municipal-parking-inventory";
  readonly position: LocalHorizontal;
  readonly groundElevationM: number;
  readonly yawRadians: number;
  readonly pitchRadians: number;
  readonly bodyColorHex: number;
  readonly family: RuntimeParkedVehicleFamily;
  readonly orientation: RuntimeParkingOrientation;
  readonly roadside: boolean;
}

export type VilniusTreeFamily =
  | "deciduous_planting"
  | "mixed_woodland";

export interface RuntimeTreeInstance {
  id: string;
  sourceId: string;
  sourceFeatureId: string;
  kind: "tree";
  family: VilniusTreeFamily;
  /** Metres relative to the owning tile origin. */
  position: LocalHorizontal;
  /** Height above the documented local vertical origin. */
  baseElevationM: number;
  heightM: number;
  crownRadiusM: number;
  /** Surveyed trunk diameter when supplied by a mapped inventory. */
  trunkDiameterM?: number;
  yawRadians: number;
  /** Official municipal inventory code when an individual surveyed tree is used. */
  speciesCode?: number;
  placement:
    | "source-land-cover-deterministic-sampling"
    | "reference-derived-street-row"
    | "official-municipal-tree-inventory"
    | "osm-mapped-tree-node";
}

export interface RuntimeRailwayEquipment {
  id: string;
  sourceId: string;
  kind: "switch" | "signal" | "buffer_stop" | "train_station_entrance";
  /** Metres relative to the owning tile origin. */
  position: LocalHorizontal;
  ref?: string;
}

export type RuntimeRoadControlKind =
  | "crossing"
  | "stop"
  | "give_way"
  | "no_entry"
  | "priority"
  | "speed_limit"
  | "parking";

export interface RuntimeRoadControl {
  id: string;
  sourceId: string;
  kind: RuntimeRoadControlKind;
  /** Exact source position or matched road-control position, tile-relative. */
  position: LocalHorizontal;
  /** Offline-matched point on the authoritative GRPK road, tile-relative. */
  roadPosition: LocalHorizontal;
  /** Unit tangent in runtime coordinates, where positive Z is south. */
  tangent: LocalHorizontal;
  /** Stable GRPK feature ID before per-tile clipping suffixes. */
  roadFeatureId: string;
  placement:
    | "matched-crossing-node"
    | "matched-road-control-node"
    | "exact-traffic-sign-node";
  value?: number;
  signCodes?: readonly string[];
  supplementaryCodes?: readonly string[];
  directionDegrees?: number;
  directionAlongRoad?: "forward" | "backward";
  marked?: boolean;
  signalized?: boolean;
  tactilePaving?: boolean;
  kerb?: "lowered" | "flush" | "raised";
  refugeIsland?: boolean;
  raisedTable?: boolean;
  cycleCrossing?: boolean;
}

export type RuntimeStreetFurnitureKind =
  | "bollard"
  | "waste_basket"
  | "bicycle_parking"
  | "street_cabinet"
  | "drain"
  | "manhole"
  | "hydrant"
  | "bench"
  | "planter";

export interface RuntimeStreetFurniture {
  id: string;
  sourceId: string;
  kind: RuntimeStreetFurnitureKind;
  position: LocalHorizontal;
  directionDegrees?: number;
  placement: "exact-osm-node";
}

export interface BuildingFootprintCollisionSpec {
  buildingId: string;
  footprint: PolygonWithHoles;
  baseElevationM: number;
  /** Absent until a source-backed height exists. */
  heightM?: number;
}

export interface RuntimeTileCollisionData {
  buildingFootprints: readonly BuildingFootprintCollisionSpec[];
}

export interface RuntimeLodBuildingBounds {
  buildingId: string;
  bounds: HorizontalBounds;
  baseElevationM: number;
}

export interface RuntimeTileFeatureCounts {
  buildings: number;
  transportLines: number;
  waterLines: number;
  landCoverMasks: number;
  collisionFootprints: number;
  vegetationInstances?: number;
  structureLines?: number;
  hardscapeAreas?: number;
  railwayEquipment?: number;
  roadControls?: number;
  streetFurniture?: number;
  parkedVehicles?: number;
}

export interface RuntimeTileLodPayload {
  level: 1 | 2;
  terrain: TerrainTileData;
  /** Source-derived bounds only; no inferred building height or massing. */
  buildingBounds: readonly RuntimeLodBuildingBounds[];
  featureCounts: RuntimeTileFeatureCounts;
}

export interface RuntimeWorldTilePayloadV2 {
  schemaVersion:
    | typeof RUNTIME_TILE_SCHEMA_VERSION
    | typeof LEGACY_RUNTIME_TILE_SCHEMA_VERSION;
  id: WorldTileId;
  address: WorldTileAddress;
  sizeM: typeof WORLD_TILE_SIZE_M;
  origin: LocalHorizontal;
  bounds: HorizontalBounds;
  localOriginLks94: LocalOrigin;
  sourceIds: readonly string[];
  terrain: TerrainTileData;
  buildings: readonly BuildingFootprintData[];
  transportLines: readonly RuntimeLinearFeature[];
  waterLines: readonly RuntimeLinearFeature[];
  landCoverMasks: readonly RuntimeAreaFeature[];
  /** Optional for backward compatibility with pre-structure schema-v2 tiles. */
  structureLines?: readonly RuntimeLinearFeature[];
  /** Optional for backward compatibility with pre-hardscape schema-v2 tiles. */
  hardscapeAreas?: readonly RuntimeAreaFeature[];
  /** Optional for backward compatibility with pre-vegetation schema-v2 tiles. */
  vegetation?: readonly RuntimeTreeInstance[];
  /** Separable exact OSM points; visual context only, with no rail logic. */
  railwayEquipment?: readonly RuntimeRailwayEquipment[];
  /** Strict offline OSM-to-GRPK matches and exact physical sign nodes. */
  roadControls?: readonly RuntimeRoadControl[];
  /** Exact mapped curb-zone objects. */
  streetFurniture?: readonly RuntimeStreetFurniture[];
  /** Required on schema v3; absent only on legacy schema-v2 payloads. */
  parkedVehicles?: readonly RuntimeParkedVehicleDescriptor[];
  collision: RuntimeTileCollisionData;
  lods: readonly [RuntimeTileLodPayload, RuntimeTileLodPayload];
}

export interface RuntimeTileManifestEntryV2 {
  id: WorldTileId;
  address: WorldTileAddress;
  bounds: HorizontalBounds;
  url: string;
  byteLength: number;
  sha256: string;
  neighborIds: readonly WorldTileId[];
  availableLods: readonly [0, 1, 2];
}

export interface RuntimeTileManifestV2 {
  schemaVersion:
    | typeof RUNTIME_TILE_SCHEMA_VERSION
    | typeof LEGACY_RUNTIME_TILE_SCHEMA_VERSION;
  aoiId: string;
  tileSizeM: typeof WORLD_TILE_SIZE_M;
  localOriginLks94: LocalOrigin;
  entryTileId: WorldTileId;
  sourceIds: readonly string[];
  tiles: readonly RuntimeTileManifestEntryV2[];
}

/**
 * Compact on-wire manifest. Tile IDs, bounds, URLs, neighbors and LODs are
 * deterministic consequences of the 256 m address grid, so shipping those
 * fields for every one of Vilnius' 15k cells only delays the first tile.
 */
export interface RuntimeTileManifestCompactV3 {
  schemaVersion: typeof RUNTIME_TILE_MANIFEST_SCHEMA_VERSION;
  tilePayloadSchemaVersion: typeof RUNTIME_TILE_SCHEMA_VERSION;
  aoiId: string;
  tileSizeM: typeof WORLD_TILE_SIZE_M;
  localOriginLks94: LocalOrigin;
  entryTileId: WorldTileId;
  sourceIds: readonly string[];
  publicBaseUrl: string;
  /** [tile x, tile z, serialized byte length, sha256] */
  tiles: readonly (readonly [number, number, number, string])[];
}

export function parseRuntimeTileManifest(
  value: unknown,
): RuntimeTileManifestV2 {
  const manifest = requireRecord(value, "runtime tile manifest");
  const isCompactManifest = manifest.tilePayloadSchemaVersion !== undefined;
  if (
    !isCompactManifest &&
    (
      manifest.schemaVersion === RUNTIME_TILE_SCHEMA_VERSION ||
      manifest.schemaVersion === LEGACY_RUNTIME_TILE_SCHEMA_VERSION
    )
  ) {
    return parseRuntimeTileManifestV2(value);
  }
  const isLegacyCompactManifest =
    manifest.schemaVersion === LEGACY_RUNTIME_TILE_MANIFEST_SCHEMA_VERSION &&
    manifest.tilePayloadSchemaVersion === LEGACY_RUNTIME_TILE_SCHEMA_VERSION;
  if (!isLegacyCompactManifest) {
    requireEqual(
      manifest.schemaVersion,
      RUNTIME_TILE_MANIFEST_SCHEMA_VERSION,
      "manifest.schemaVersion",
    );
    requireEqual(
      manifest.tilePayloadSchemaVersion,
      RUNTIME_TILE_SCHEMA_VERSION,
      "manifest.tilePayloadSchemaVersion",
    );
  }
  requireEqual(manifest.tileSizeM, WORLD_TILE_SIZE_M, "manifest.tileSizeM");
  requireNonEmptyString(manifest.aoiId, "manifest.aoiId");
  requireNonEmptyString(manifest.entryTileId, "manifest.entryTileId");
  requireStringArray(manifest.sourceIds, "manifest.sourceIds");
  requireLocalOrigin(manifest.localOriginLks94, "manifest.localOriginLks94");
  requireNonEmptyString(manifest.publicBaseUrl, "manifest.publicBaseUrl");
  const publicBaseUrl = (manifest.publicBaseUrl as string).replace(/\/+$/u, "");
  const rows = requireArray(manifest.tiles, "manifest.tiles");
  if (rows.length === 0) {
    throw new Error("manifest.tiles must not be empty");
  }
  const addresses: WorldTileAddress[] = [];
  const integrity: { byteLength: number; sha256: string }[] = [];
  const ids = new Set<WorldTileId>();
  for (const [index, rawRow] of rows.entries()) {
    const row = requireArray(rawRow, `manifest.tiles[${index}]`);
    if (row.length !== 4) {
      throw new Error(`manifest.tiles[${index}] must contain four values`);
    }
    const [x, z, byteLength, sha256] = row;
    if (!Number.isInteger(x) || !Number.isInteger(z)) {
      throw new Error(`manifest.tiles[${index}] address must use integers`);
    }
    requirePositiveInteger(byteLength, `manifest.tiles[${index}][2]`);
    requireSha256(sha256, `manifest.tiles[${index}][3]`);
    const address = { x: x as number, z: z as number };
    const id = worldTileId(address);
    if (ids.has(id)) {
      throw new Error(`manifest.tiles contains duplicate tile ${id}`);
    }
    ids.add(id);
    addresses.push(address);
    integrity.push({ byteLength: byteLength as number, sha256: sha256 as string });
  }
  if (!ids.has(manifest.entryTileId as WorldTileId)) {
    throw new Error("manifest.entryTileId must reference a manifest tile");
  }
  const tiles: RuntimeTileManifestEntryV2[] = addresses.map((address, index) => {
    const id = worldTileId(address);
    const tileIntegrity = integrity[index]!;
    return {
      id,
      address,
      bounds: {
        minX: address.x * WORLD_TILE_SIZE_M,
        minZ: address.z * WORLD_TILE_SIZE_M,
        maxX: (address.x + 1) * WORLD_TILE_SIZE_M,
        maxZ: (address.z + 1) * WORLD_TILE_SIZE_M,
      },
      url: `${publicBaseUrl}/tiles/${address.x}_${address.z}.json`,
      byteLength: tileIntegrity.byteLength,
      sha256: tileIntegrity.sha256,
      neighborIds: [
        worldTileId({ x: address.x - 1, z: address.z }),
        worldTileId({ x: address.x + 1, z: address.z }),
        worldTileId({ x: address.x, z: address.z - 1 }),
        worldTileId({ x: address.x, z: address.z + 1 }),
      ].filter((neighborId) => ids.has(neighborId)).sort(compareCodeUnits),
      availableLods: [0, 1, 2],
    };
  });
  return {
    schemaVersion: RUNTIME_TILE_SCHEMA_VERSION,
    aoiId: manifest.aoiId as string,
    tileSizeM: WORLD_TILE_SIZE_M,
    localOriginLks94: manifest.localOriginLks94 as LocalOrigin,
    entryTileId: manifest.entryTileId as WorldTileId,
    sourceIds: manifest.sourceIds as string[],
    tiles,
  };
}

export function parseRuntimeTileManifestV2(
  value: unknown,
): RuntimeTileManifestV2 {
  const manifest = requireRecord(value, "runtime tile manifest");
  if (
    manifest.schemaVersion !== RUNTIME_TILE_SCHEMA_VERSION &&
    manifest.schemaVersion !== LEGACY_RUNTIME_TILE_SCHEMA_VERSION
  ) {
    throw new Error(
      `manifest.schemaVersion must equal ${LEGACY_RUNTIME_TILE_SCHEMA_VERSION} or ${RUNTIME_TILE_SCHEMA_VERSION}`,
    );
  }
  requireEqual(manifest.tileSizeM, WORLD_TILE_SIZE_M, "manifest.tileSizeM");
  requireNonEmptyString(manifest.aoiId, "manifest.aoiId");
  requireNonEmptyString(manifest.entryTileId, "manifest.entryTileId");
  requireStringArray(manifest.sourceIds, "manifest.sourceIds");
  requireLocalOrigin(manifest.localOriginLks94, "manifest.localOriginLks94");
  const tiles = requireArray(manifest.tiles, "manifest.tiles");
  if (tiles.length === 0) {
    throw new Error("manifest.tiles must not be empty");
  }
  for (const [index, tile] of tiles.entries()) {
    const entry = requireRecord(tile, `manifest.tiles[${index}]`);
    requireNonEmptyString(entry.id, `manifest.tiles[${index}].id`);
    requireAddress(entry.address, `manifest.tiles[${index}].address`);
    requireBounds(entry.bounds, `manifest.tiles[${index}].bounds`);
    requireNonEmptyString(entry.url, `manifest.tiles[${index}].url`);
    requirePositiveInteger(
      entry.byteLength,
      `manifest.tiles[${index}].byteLength`,
    );
    requireSha256(entry.sha256, `manifest.tiles[${index}].sha256`);
    requireStringArray(
      entry.neighborIds,
      `manifest.tiles[${index}].neighborIds`,
    );
    const lods = requireArray(
      entry.availableLods,
      `manifest.tiles[${index}].availableLods`,
    );
    if (lods.length !== 3 || lods[0] !== 0 || lods[1] !== 1 || lods[2] !== 2) {
      throw new Error(
        `manifest.tiles[${index}].availableLods must equal [0,1,2]`,
      );
    }
  }
  if (
    !tiles.some((tile) =>
      requireRecord(tile, "manifest tile").id === manifest.entryTileId
    )
  ) {
    throw new Error("manifest.entryTileId must reference a manifest tile");
  }
  return value as RuntimeTileManifestV2;
}

/**
 * Join independently buildable sparse world exports into one streaming
 * catalog. Tile URLs remain owned by their source manifests, while adjacency
 * is recomputed across export boundaries so a continuation attached to the
 * broad city behaves like one world.
 */
export function mergeRuntimeTileManifests(
  manifests: readonly Readonly<RuntimeTileManifestV2>[],
): RuntimeTileManifestV2 {
  const base = manifests[0];
  if (base === undefined) {
    throw new Error("At least one runtime tile manifest is required");
  }
  const tilesById = new Map<WorldTileId, RuntimeTileManifestEntryV2>();
  for (const manifest of manifests) {
    if (manifest.tileSizeM !== base.tileSizeM) {
      throw new Error(
        `Runtime tile manifest ${manifest.aoiId} uses a different tile size`,
      );
    }
    if (
      manifest.localOriginLks94.crs !== base.localOriginLks94.crs ||
      manifest.localOriginLks94.easting !==
        base.localOriginLks94.easting ||
      manifest.localOriginLks94.northing !==
        base.localOriginLks94.northing ||
      manifest.localOriginLks94.elevationM !==
        base.localOriginLks94.elevationM
    ) {
      throw new Error(
        `Runtime tile manifest ${manifest.aoiId} uses a different local origin`,
      );
    }
    for (const tile of manifest.tiles) {
      if (tilesById.has(tile.id)) {
        throw new Error(`Duplicate runtime tile across manifests: ${tile.id}`);
      }
      tilesById.set(tile.id, tile);
    }
  }
  const ids = new Set(tilesById.keys());
  const tiles = [...tilesById.values()]
    .sort((left, right) =>
      left.address.z - right.address.z || left.address.x - right.address.x
    )
    .map((tile) => ({
      ...tile,
      neighborIds: [
        worldTileId({ x: tile.address.x - 1, z: tile.address.z }),
        worldTileId({ x: tile.address.x + 1, z: tile.address.z }),
        worldTileId({ x: tile.address.x, z: tile.address.z - 1 }),
        worldTileId({ x: tile.address.x, z: tile.address.z + 1 }),
      ].filter((id) => ids.has(id)).sort(compareCodeUnits),
    }));
  return {
    schemaVersion: RUNTIME_TILE_SCHEMA_VERSION,
    aoiId: manifests.map((manifest) => manifest.aoiId).join("+"),
    tileSizeM: base.tileSizeM,
    localOriginLks94: base.localOriginLks94,
    entryTileId: base.entryTileId,
    sourceIds: [...new Set(manifests.flatMap((manifest) =>
      manifest.sourceIds
    ))].sort(compareCodeUnits),
    tiles,
  };
}

function compareCodeUnits(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

export function parseRuntimeWorldTilePayloadV2(
  value: unknown,
): RuntimeWorldTilePayloadV2 {
  const tile = requireRecord(value, "runtime tile payload");
  if (
    tile.schemaVersion !== RUNTIME_TILE_SCHEMA_VERSION &&
    tile.schemaVersion !== LEGACY_RUNTIME_TILE_SCHEMA_VERSION
  ) {
    throw new Error(
      `tile.schemaVersion must equal ${LEGACY_RUNTIME_TILE_SCHEMA_VERSION} or ${RUNTIME_TILE_SCHEMA_VERSION}`,
    );
  }
  requireEqual(tile.sizeM, WORLD_TILE_SIZE_M, "tile.sizeM");
  requireNonEmptyString(tile.id, "tile.id");
  requireAddress(tile.address, "tile.address");
  requirePoint(tile.origin, "tile.origin");
  requireBounds(tile.bounds, "tile.bounds");
  requireLocalOrigin(tile.localOriginLks94, "tile.localOriginLks94");
  requireStringArray(tile.sourceIds, "tile.sourceIds");
  requireTerrain(tile.terrain, "tile.terrain");
  requireArray(tile.buildings, "tile.buildings");
  requireLinearFeatures(tile.transportLines, "tile.transportLines", [
    "road",
    "path",
    "railway",
  ]);
  requireLinearFeatures(tile.waterLines, "tile.waterLines", ["water"]);
  requireAreaFeatures(tile.landCoverMasks, "tile.landCoverMasks", "land-cover");
  if (tile.structureLines !== undefined) {
    requireLinearFeatures(tile.structureLines, "tile.structureLines", [
      "structure",
    ]);
  }
  if (tile.hardscapeAreas !== undefined) {
    requireAreaFeatures(tile.hardscapeAreas, "tile.hardscapeAreas", "hardscape");
  }
  if (tile.vegetation !== undefined) {
    requireTreeInstances(tile.vegetation, "tile.vegetation");
  }
  if (tile.railwayEquipment !== undefined) {
    requireRailwayEquipment(
      tile.railwayEquipment,
      "tile.railwayEquipment",
    );
  }
  if (tile.roadControls !== undefined) {
    requireRoadControls(tile.roadControls, "tile.roadControls");
  }
  if (tile.streetFurniture !== undefined) {
    requireStreetFurniture(tile.streetFurniture, "tile.streetFurniture");
  }
  if (tile.schemaVersion === RUNTIME_TILE_SCHEMA_VERSION) {
    requireParkedVehicles(tile.parkedVehicles, "tile.parkedVehicles");
  } else if (tile.parkedVehicles !== undefined) {
    requireParkedVehicles(tile.parkedVehicles, "tile.parkedVehicles");
  }

  const collision = requireRecord(tile.collision, "tile.collision");
  const collisionFootprints = requireArray(
    collision.buildingFootprints,
    "tile.collision.buildingFootprints",
  );
  for (const [index, rawSpec] of collisionFootprints.entries()) {
    const spec = requireRecord(
      rawSpec,
      `tile.collision.buildingFootprints[${index}]`,
    );
    requireNonEmptyString(
      spec.buildingId,
      `tile.collision.buildingFootprints[${index}].buildingId`,
    );
    requirePolygon(
      spec.footprint,
      `tile.collision.buildingFootprints[${index}].footprint`,
    );
    requireFiniteNumber(
      spec.baseElevationM,
      `tile.collision.buildingFootprints[${index}].baseElevationM`,
    );
    if (spec.heightM !== undefined) {
      requirePositiveNumber(
        spec.heightM,
        `tile.collision.buildingFootprints[${index}].heightM`,
      );
    }
  }

  const lods = requireArray(tile.lods, "tile.lods");
  if (lods.length !== 2) {
    throw new Error("tile.lods must contain levels 1 and 2");
  }
  for (const [index, rawLod] of lods.entries()) {
    const lod = requireRecord(rawLod, `tile.lods[${index}]`);
    if (lod.level !== index + 1) {
      throw new Error(`tile.lods[${index}].level must equal ${index + 1}`);
    }
    requireTerrain(lod.terrain, `tile.lods[${index}].terrain`);
    requireArray(lod.buildingBounds, `tile.lods[${index}].buildingBounds`);
    requireRecord(lod.featureCounts, `tile.lods[${index}].featureCounts`);
  }
  return value as RuntimeWorldTilePayloadV2;
}

function requireParkedVehicles(value: unknown, path: string): void {
  const supportedFamilies = new Set<RuntimeParkedVehicleFamily>([
    "compact-hatch",
    "estate-wagon",
    "ordinary-sedan",
    "small-crossover",
    "compact-van",
    "older-boxy-car",
  ]);
  const supportedOrientations = new Set<RuntimeParkingOrientation>([
    "parallel",
    "diagonal",
    "perpendicular",
  ]);
  const supportedProvenance = new Set<
    RuntimeParkedVehicleDescriptor["provenance"]
  >([
    "osm-explicit-parking-space",
    "osm-explicit-street-parking",
    "osm-mapped-parking-area",
    "official-orthophoto-reviewed-row",
    "official-orthophoto-detected-vehicle",
    "official-municipal-parking-inventory",
  ]);
  const ids = new Set<string>();
  for (const [index, rawDescriptor] of requireArray(value, path).entries()) {
    const descriptor = requireRecord(rawDescriptor, `${path}[${index}]`);
    const id = requireNonEmptyString(descriptor.id, `${path}[${index}].id`);
    if (ids.has(id)) {
      throw new Error(`${path} contains duplicate parked vehicle ${id}`);
    }
    ids.add(id);
    requireEqual(
      descriptor.kind,
      "parked-vehicle",
      `${path}[${index}].kind`,
    );
    requireNonEmptyString(descriptor.sourceId, `${path}[${index}].sourceId`);
    requireNonEmptyString(
      descriptor.sourceFeatureId,
      `${path}[${index}].sourceFeatureId`,
    );
    if (
      !supportedProvenance.has(
        descriptor.provenance as RuntimeParkedVehicleDescriptor["provenance"],
      )
    ) {
      throw new Error(`${path}[${index}].provenance is unsupported`);
    }
    requirePoint(descriptor.position, `${path}[${index}].position`);
    requireFiniteNumber(
      descriptor.groundElevationM,
      `${path}[${index}].groundElevationM`,
    );
    requireFiniteNumber(
      descriptor.yawRadians,
      `${path}[${index}].yawRadians`,
    );
    requireFiniteNumber(
      descriptor.pitchRadians,
      `${path}[${index}].pitchRadians`,
    );
    if (
      !Number.isInteger(descriptor.bodyColorHex) ||
      (descriptor.bodyColorHex as number) < 0 ||
      (descriptor.bodyColorHex as number) > 0xffffff
    ) {
      throw new Error(`${path}[${index}].bodyColorHex must be a 24-bit integer`);
    }
    if (
      !supportedFamilies.has(descriptor.family as RuntimeParkedVehicleFamily)
    ) {
      throw new Error(`${path}[${index}].family is unsupported`);
    }
    if (
      !supportedOrientations.has(
        descriptor.orientation as RuntimeParkingOrientation,
      )
    ) {
      throw new Error(`${path}[${index}].orientation is unsupported`);
    }
    if (typeof descriptor.roadside !== "boolean") {
      throw new Error(`${path}[${index}].roadside must be a boolean`);
    }
  }
}

function requireTreeInstances(value: unknown, path: string): void {
  for (const [index, rawInstance] of requireArray(value, path).entries()) {
    const instance = requireRecord(rawInstance, `${path}[${index}]`);
    requireNonEmptyString(instance.id, `${path}[${index}].id`);
    requireNonEmptyString(instance.sourceId, `${path}[${index}].sourceId`);
    requireNonEmptyString(
      instance.sourceFeatureId,
      `${path}[${index}].sourceFeatureId`,
    );
    requireEqual(instance.kind, "tree", `${path}[${index}].kind`);
    if (
      instance.family !== "deciduous_planting" &&
      instance.family !== "mixed_woodland"
    ) {
      throw new Error(`${path}[${index}].family is unsupported`);
    }
    requirePoint(instance.position, `${path}[${index}].position`);
    requireFiniteNumber(
      instance.baseElevationM,
      `${path}[${index}].baseElevationM`,
    );
    requirePositiveNumber(instance.heightM, `${path}[${index}].heightM`);
    requirePositiveNumber(
      instance.crownRadiusM,
      `${path}[${index}].crownRadiusM`,
    );
    if (instance.trunkDiameterM !== undefined) {
      requirePositiveNumber(
        instance.trunkDiameterM,
        `${path}[${index}].trunkDiameterM`,
      );
    }
    requireFiniteNumber(
      instance.yawRadians,
      `${path}[${index}].yawRadians`,
    );
    if (instance.speciesCode !== undefined) {
      requirePositiveNumber(
        instance.speciesCode,
        `${path}[${index}].speciesCode`,
      );
    }
    if (
      instance.placement !== "source-land-cover-deterministic-sampling" &&
      instance.placement !== "reference-derived-street-row" &&
      instance.placement !== "official-municipal-tree-inventory" &&
      instance.placement !== "osm-mapped-tree-node"
    ) {
      throw new Error(`${path}[${index}].placement is unsupported`);
    }
  }
}

function requireRoadControls(value: unknown, path: string): void {
  for (const [index, rawControl] of requireArray(value, path).entries()) {
    const itemPath = `${path}[${index}]`;
    const control = requireRecord(rawControl, itemPath);
    requireNonEmptyString(control.id, `${itemPath}.id`);
    requireNonEmptyString(control.sourceId, `${itemPath}.sourceId`);
    if (![
      "crossing",
      "stop",
      "give_way",
      "no_entry",
      "priority",
      "speed_limit",
      "parking",
    ].includes(String(control.kind))) {
      throw new Error(`${itemPath}.kind is unsupported`);
    }
    requirePoint(control.position, `${itemPath}.position`);
    requirePoint(control.roadPosition, `${itemPath}.roadPosition`);
    requirePoint(control.tangent, `${itemPath}.tangent`);
    const tangent = control.tangent as { x: number; z: number };
    const tangentLength = Math.hypot(tangent.x, tangent.z);
    if (Math.abs(tangentLength - 1) > 0.01) {
      throw new Error(`${itemPath}.tangent must be unit length`);
    }
    requireNonEmptyString(control.roadFeatureId, `${itemPath}.roadFeatureId`);
    if (![
      "matched-crossing-node",
      "matched-road-control-node",
      "exact-traffic-sign-node",
    ].includes(String(control.placement))) {
      throw new Error(`${itemPath}.placement is unsupported`);
    }
    if (control.value !== undefined) {
      requirePositiveNumber(control.value, `${itemPath}.value`);
    }
    if (control.signCodes !== undefined) {
      requireStringArray(control.signCodes, `${itemPath}.signCodes`);
    }
    if (control.supplementaryCodes !== undefined) {
      requireStringArray(
        control.supplementaryCodes,
        `${itemPath}.supplementaryCodes`,
      );
    }
    if (control.directionDegrees !== undefined) {
      requireFiniteNumber(
        control.directionDegrees,
        `${itemPath}.directionDegrees`,
      );
    }
    if (
      control.directionAlongRoad !== undefined &&
      control.directionAlongRoad !== "forward" &&
      control.directionAlongRoad !== "backward"
    ) {
      throw new Error(`${itemPath}.directionAlongRoad is unsupported`);
    }
    for (const key of [
      "marked",
      "signalized",
      "tactilePaving",
      "refugeIsland",
      "raisedTable",
      "cycleCrossing",
    ]) {
      if (control[key] !== undefined && typeof control[key] !== "boolean") {
        throw new Error(`${itemPath}.${key} must be boolean`);
      }
    }
    if (
      control.kerb !== undefined &&
      !["lowered", "flush", "raised"].includes(String(control.kerb))
    ) {
      throw new Error(`${itemPath}.kerb is unsupported`);
    }
  }
}

function requireStreetFurniture(value: unknown, path: string): void {
  for (const [index, rawItem] of requireArray(value, path).entries()) {
    const itemPath = `${path}[${index}]`;
    const item = requireRecord(rawItem, itemPath);
    requireNonEmptyString(item.id, `${itemPath}.id`);
    requireNonEmptyString(item.sourceId, `${itemPath}.sourceId`);
    if (![
      "bollard",
      "waste_basket",
      "bicycle_parking",
      "street_cabinet",
      "drain",
      "manhole",
      "hydrant",
      "bench",
      "planter",
    ].includes(String(item.kind))) {
      throw new Error(`${itemPath}.kind is unsupported`);
    }
    requirePoint(item.position, `${itemPath}.position`);
    requireEqual(item.placement, "exact-osm-node", `${itemPath}.placement`);
    if (item.directionDegrees !== undefined) {
      requireFiniteNumber(item.directionDegrees, `${itemPath}.directionDegrees`);
    }
  }
}

function requireLinearFeatures(
  value: unknown,
  path: string,
  acceptedKinds: readonly string[],
): void {
  for (const [index, rawFeature] of requireArray(value, path).entries()) {
    const feature = requireRecord(rawFeature, `${path}[${index}]`);
    requireNonEmptyString(feature.id, `${path}[${index}].id`);
    requireNonEmptyString(feature.sourceId, `${path}[${index}].sourceId`);
    if (
      typeof feature.kind !== "string" ||
      !acceptedKinds.includes(feature.kind)
    ) {
      throw new Error(`${path}[${index}].kind is unsupported`);
    }
    requireNonEmptyString(feature.classCode, `${path}[${index}].classCode`);
    if (feature.name !== undefined) {
      requireNonEmptyString(feature.name, `${path}[${index}].name`);
    }
    if (feature.carriagewayWidthM !== undefined) {
      requirePositiveNumber(
        feature.carriagewayWidthM,
        `${path}[${index}].carriagewayWidthM`,
      );
    }
    if (feature.totalWidthM !== undefined) {
      requirePositiveNumber(
        feature.totalWidthM,
        `${path}[${index}].totalWidthM`,
      );
    }
    if (feature.publicRealmWidthM !== undefined) {
      requirePositiveNumber(
        feature.publicRealmWidthM,
        `${path}[${index}].publicRealmWidthM`,
      );
      if (feature.kind !== "road") {
        throw new Error(
          `${path}[${index}].publicRealmWidthM is supported only on roads`,
        );
      }
    }
    if (feature.pedestrianStreetFullWidth !== undefined) {
      if (typeof feature.pedestrianStreetFullWidth !== "boolean") {
        throw new Error(
          `${path}[${index}].pedestrianStreetFullWidth must be a boolean`,
        );
      }
      if (
        feature.pedestrianStreetFullWidth === true &&
        feature.widthM === undefined
      ) {
        throw new Error(
          `${path}[${index}].pedestrianStreetFullWidth requires widthM`,
        );
      }
    }
    if (feature.surfaceCode !== undefined) {
      requireNonEmptyString(
        feature.surfaceCode,
        `${path}[${index}].surfaceCode`,
      );
    }
    if (feature.sidewalkMode !== undefined) {
      if (
        feature.sidewalkMode !== "both" &&
        feature.sidewalkMode !== "left" &&
        feature.sidewalkMode !== "right" &&
        feature.sidewalkMode !== "none" &&
        feature.sidewalkMode !== "separate"
      ) {
        throw new Error(`${path}[${index}].sidewalkMode is unsupported`);
      }
    }
    if (feature.sidewalkSourceId !== undefined) {
      requireNonEmptyString(
        feature.sidewalkSourceId,
        `${path}[${index}].sidewalkSourceId`,
      );
    }
    if (feature.roadEvidenceSourceId !== undefined) {
      requireNonEmptyString(
        feature.roadEvidenceSourceId,
        `${path}[${index}].roadEvidenceSourceId`,
      );
    }
    for (const key of [
      "osmWayId",
      "lanes",
      "lanesForward",
      "lanesBackward",
    ]) {
      if (feature[key] !== undefined) {
        requirePositiveInteger(feature[key], `${path}[${index}].${key}`);
      }
    }
    for (const key of [
      "turnLanesForward",
      "turnLanesBackward",
      "changeLanesForward",
      "changeLanesBackward",
      "busLanesForward",
      "busLanesBackward",
    ]) {
      if (feature[key] !== undefined) {
        requireStringArray(feature[key], `${path}[${index}].${key}`);
      }
    }
    if (
      feature.onewayDirection !== undefined &&
      feature.onewayDirection !== "forward" &&
      feature.onewayDirection !== "backward"
    ) {
      throw new Error(`${path}[${index}].onewayDirection is unsupported`);
    }
    if (feature.maxspeedKph !== undefined) {
      requirePositiveNumber(
        feature.maxspeedKph,
        `${path}[${index}].maxspeedKph`,
      );
    }
    for (const key of ["cycleLaneLeft", "cycleLaneRight"]) {
      if (feature[key] !== undefined && typeof feature[key] !== "boolean") {
        throw new Error(`${path}[${index}].${key} must be boolean`);
      }
    }
    for (const key of ["parkingLeft", "parkingRight"]) {
      if (
        feature[key] !== undefined &&
        feature[key] !== "parallel" &&
        feature[key] !== "marked"
      ) {
        throw new Error(`${path}[${index}].${key} is unsupported`);
      }
    }
    const hasRoadEvidence = feature.osmWayId !== undefined ||
      feature.lanes !== undefined || feature.lanesForward !== undefined ||
      feature.lanesBackward !== undefined ||
      feature.turnLanesForward !== undefined ||
      feature.turnLanesBackward !== undefined ||
      feature.changeLanesForward !== undefined ||
      feature.changeLanesBackward !== undefined ||
      feature.onewayDirection !== undefined || feature.maxspeedKph !== undefined ||
      feature.busLanesForward !== undefined ||
      feature.busLanesBackward !== undefined ||
      feature.cycleLaneLeft !== undefined ||
      feature.cycleLaneRight !== undefined || feature.parkingLeft !== undefined ||
      feature.parkingRight !== undefined;
    if (hasRoadEvidence !== (feature.roadEvidenceSourceId !== undefined)) {
      throw new Error(
        `${path}[${index}] road evidence fields must be source-paired`,
      );
    }
    if (feature.railwayEvidenceSourceId !== undefined) {
      requireNonEmptyString(
        feature.railwayEvidenceSourceId,
        `${path}[${index}].railwayEvidenceSourceId`,
      );
    }
    if (
      feature.railwayUsage !== undefined &&
      !["main", "branch", "industrial", "military", "tourism"].includes(
        feature.railwayUsage as string,
      )
    ) {
      throw new Error(`${path}[${index}].railwayUsage is unsupported`);
    }
    if (
      feature.railwayService !== undefined &&
      !["siding", "spur", "yard", "crossover"].includes(
        feature.railwayService as string,
      )
    ) {
      throw new Error(`${path}[${index}].railwayService is unsupported`);
    }
    if (
      feature.railwayElectrified !== undefined &&
      !["contact_line", "rail", "no"].includes(
        feature.railwayElectrified as string,
      )
    ) {
      throw new Error(`${path}[${index}].railwayElectrified is unsupported`);
    }
    if (feature.railwayGaugeM !== undefined) {
      requirePositiveNumber(
        feature.railwayGaugeM,
        `${path}[${index}].railwayGaugeM`,
      );
    }
    const railwayBooleanKeys = [
      "railwayBridge",
      "railwayTunnel",
      "railwayEmbankment",
    ] as const;
    for (const key of railwayBooleanKeys) {
      if (feature[key] !== undefined && typeof feature[key] !== "boolean") {
        throw new Error(`${path}[${index}].${key} must be a boolean`);
      }
    }
    const hasRailwaySemantics = feature.railwayUsage !== undefined ||
      feature.railwayService !== undefined ||
      feature.railwayElectrified !== undefined ||
      feature.railwayGaugeM !== undefined ||
      feature.railwayBridge !== undefined ||
      feature.railwayTunnel !== undefined ||
      feature.railwayEmbankment !== undefined;
    if (
      hasRailwaySemantics !==
        (feature.railwayEvidenceSourceId !== undefined)
    ) {
      throw new Error(
        `${path}[${index}] railway evidence fields must be source-paired`,
      );
    }
    if (
      (feature.sidewalkMode === undefined) !==
        (feature.sidewalkSourceId === undefined)
    ) {
      throw new Error(
        `${path}[${index}] sidewalk evidence fields must be paired`,
      );
    }
    if (feature.widthM !== undefined) {
      requirePositiveNumber(
        feature.widthM,
        `${path}[${index}].widthM`,
      );
    }
    if (feature.heightM !== undefined) {
      requirePositiveNumber(
        feature.heightM,
        `${path}[${index}].heightM`,
      );
    }
    if (feature.parking !== undefined) {
      requireParkingMetadata(feature.parking, `${path}[${index}].parking`);
    }
    const points = requireArray(feature.points, `${path}[${index}].points`);
    if (points.length < 2) {
      throw new Error(`${path}[${index}].points needs at least two points`);
    }
    for (const [pointIndex, point] of points.entries()) {
      requirePoint(point, `${path}[${index}].points[${pointIndex}]`);
    }
  }
}

function requireRailwayEquipment(value: unknown, path: string): void {
  for (const [index, rawEquipment] of requireArray(value, path).entries()) {
    const equipment = requireRecord(rawEquipment, `${path}[${index}]`);
    requireNonEmptyString(equipment.id, `${path}[${index}].id`);
    requireNonEmptyString(equipment.sourceId, `${path}[${index}].sourceId`);
    if (
      typeof equipment.kind !== "string" ||
      !["switch", "signal", "buffer_stop", "train_station_entrance"].includes(
        equipment.kind,
      )
    ) {
      throw new Error(`${path}[${index}].kind is unsupported`);
    }
    requirePoint(equipment.position, `${path}[${index}].position`);
    if (equipment.ref !== undefined) {
      requireNonEmptyString(equipment.ref, `${path}[${index}].ref`);
    }
  }
}

function requireAreaFeatures(
  value: unknown,
  path: string,
  acceptedKind: RuntimeAreaFeature["kind"],
): void {
  for (const [index, rawFeature] of requireArray(value, path).entries()) {
    const feature = requireRecord(rawFeature, `${path}[${index}]`);
    requireNonEmptyString(feature.id, `${path}[${index}].id`);
    requireNonEmptyString(feature.sourceId, `${path}[${index}].sourceId`);
    requireEqual(feature.kind, acceptedKind, `${path}[${index}].kind`);
    requireNonEmptyString(feature.classCode, `${path}[${index}].classCode`);
    if (feature.name !== undefined) {
      requireNonEmptyString(feature.name, `${path}[${index}].name`);
    }
    if (feature.parking !== undefined) {
      requireParkingMetadata(feature.parking, `${path}[${index}].parking`);
    }
    const polygons = requireArray(
      feature.polygons,
      `${path}[${index}].polygons`,
    );
    if (polygons.length === 0) {
      throw new Error(`${path}[${index}].polygons must not be empty`);
    }
    for (const [polygonIndex, polygon] of polygons.entries()) {
      requirePolygon(
        polygon,
        `${path}[${index}].polygons[${polygonIndex}]`,
      );
    }
  }
}

function requireParkingMetadata(value: unknown, path: string): void {
  const parking = requireRecord(value, path);
  if (
    parking.side !== undefined &&
    !["left", "right", "both"].includes(String(parking.side))
  ) {
    throw new Error(`${path}.side is unsupported`);
  }
  if (
    ![
      "lane",
      "street_side",
      "on_kerb",
      "half_on_kerb",
      "shoulder",
      "parking_area",
      "parking_space",
    ].includes(String(parking.placement))
  ) {
    throw new Error(`${path}.placement is unsupported`);
  }
  if (
    parking.orientation !== undefined &&
    !["parallel", "diagonal", "perpendicular"].includes(
      String(parking.orientation),
    )
  ) {
    throw new Error(`${path}.orientation is unsupported`);
  }
  if (parking.capacity !== undefined) {
    requirePositiveInteger(parking.capacity, `${path}.capacity`);
  }
  for (const key of ["access", "restriction"] as const) {
    if (parking[key] !== undefined) {
      requireNonEmptyString(parking[key], `${path}.${key}`);
    }
  }
  if (parking.staggered !== undefined && typeof parking.staggered !== "boolean") {
    throw new Error(`${path}.staggered must be a boolean`);
  }
}

function requireTerrain(value: unknown, path: string): void {
  const terrain = requireRecord(value, path);
  const side = requirePositiveInteger(
    terrain.samplesPerSide,
    `${path}.samplesPerSide`,
  );
  requirePositiveNumber(terrain.spacingM, `${path}.spacingM`);
  const heights = requireArray(terrain.heightsM, `${path}.heightsM`);
  if (heights.length !== side * side) {
    throw new Error(`${path}.heightsM has the wrong sample count`);
  }
  for (const [index, height] of heights.entries()) {
    requireFiniteNumber(height, `${path}.heightsM[${index}]`);
  }
}

function requirePolygon(value: unknown, path: string): void {
  const polygon = requireRecord(value, path);
  requireRing(polygon.outer, `${path}.outer`);
  const holes = requireArray(polygon.holes, `${path}.holes`);
  for (const [index, hole] of holes.entries()) {
    requireRing(hole, `${path}.holes[${index}]`);
  }
}

function requireRing(value: unknown, path: string): void {
  const ring = requireArray(value, path);
  if (ring.length < 4) {
    throw new Error(`${path} must have at least four points`);
  }
  for (const [index, point] of ring.entries()) {
    requirePoint(point, `${path}[${index}]`);
  }
}

function requirePoint(value: unknown, path: string): void {
  const point = requireRecord(value, path);
  requireFiniteNumber(point.x, `${path}.x`);
  requireFiniteNumber(point.z, `${path}.z`);
}

function requireBounds(value: unknown, path: string): void {
  const bounds = requireRecord(value, path);
  requireFiniteNumber(bounds.minX, `${path}.minX`);
  requireFiniteNumber(bounds.minZ, `${path}.minZ`);
  requireFiniteNumber(bounds.maxX, `${path}.maxX`);
  requireFiniteNumber(bounds.maxZ, `${path}.maxZ`);
}

function requireAddress(value: unknown, path: string): void {
  const address = requireRecord(value, path);
  requireSafeInteger(address.x, `${path}.x`);
  requireSafeInteger(address.z, `${path}.z`);
}

function requireLocalOrigin(value: unknown, path: string): void {
  const origin = requireRecord(value, path);
  requireEqual(origin.crs, "EPSG:3346", `${path}.crs`);
  requireFiniteNumber(origin.easting, `${path}.easting`);
  requireFiniteNumber(origin.northing, `${path}.northing`);
  requireFiniteNumber(origin.elevationM, `${path}.elevationM`);
}

function requireStringArray(value: unknown, path: string): void {
  for (const [index, item] of requireArray(value, path).entries()) {
    requireNonEmptyString(item, `${path}[${index}]`);
  }
}

function requireRecord(
  value: unknown,
  path: string,
): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

function requireArray(value: unknown, path: string): readonly unknown[] {
  if (!Array.isArray(value)) {
    throw new Error(`${path} must be an array`);
  }
  return value;
}

function requireNonEmptyString(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`${path} must be a non-empty string`);
  }
  return value;
}

function requireFiniteNumber(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`${path} must be finite`);
  }
  return value;
}

function requireBoolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") {
    throw new Error(`${path} must be a boolean`);
  }
  return value;
}

function requirePositiveNumber(value: unknown, path: string): number {
  const result = requireFiniteNumber(value, path);
  if (result <= 0) {
    throw new Error(`${path} must be positive`);
  }
  return result;
}

function requireSafeInteger(value: unknown, path: string): number {
  const result = requireFiniteNumber(value, path);
  if (!Number.isSafeInteger(result)) {
    throw new Error(`${path} must be a safe integer`);
  }
  return result;
}

function requirePositiveInteger(value: unknown, path: string): number {
  const result = requireSafeInteger(value, path);
  if (result <= 0) {
    throw new Error(`${path} must be positive`);
  }
  return result;
}

function requireSha256(value: unknown, path: string): void {
  const result = requireNonEmptyString(value, path);
  if (!/^[a-f0-9]{64}$/.test(result)) {
    throw new Error(`${path} must be a lowercase SHA-256 digest`);
  }
}

function requireEqual(
  value: unknown,
  expected: string | number,
  path: string,
): void {
  if (value !== expected) {
    throw new Error(`${path} must equal ${String(expected)}`);
  }
}
